/**
 * Canonical universal_v1 bot_settings defaults (universal_bot_settings_defaults.json).
 * id / user_id / email are set by the backend on save.
 * Regenerate via: python backend/scripts/export_universal_defaults_to_js.py
 * (alias: export_renovation_defaults_to_js.py)
 */

export const CANONICAL_APPOINTMENT_TYPE_KEYS = [
  "free_quote_inspection",
  "phone_video_consult"
];

export const RENOVATION_VISIT_APPOINTMENT_TYPE = "free_quote_inspection";
export const RENOVATION_CALL_APPOINTMENT_TYPE = "phone_video_consult";
export const UNIVERSAL_VISIT_APPOINTMENT_TYPE = RENOVATION_VISIT_APPOINTMENT_TYPE;
export const UNIVERSAL_CALL_APPOINTMENT_TYPE = RENOVATION_CALL_APPOINTMENT_TYPE;

export const CANONICAL_HANDOFF_ROLE_TYPES = [
  "general",
  "emergency",
  "warranty",
  "commercial"
];

export const LEGACY_APPOINTMENT_TYPE_KEYS = [
  "free_measure_and_quote",
  "design_consultation",
  "kitchen_renovation_quote",
  "bathroom_renovation_quote",
  "emergency_make_safe",
  "emergency_leak_callout",
  "restoration_consult",
  "repair_booking",
  "warranty_callback",
  "annual_maintenance_booking",
  "insurance_assessment",
  "commercial_site_walk"
];

export const CANONICAL_DISQUAL_ENUMS = {
  unqualified_action: ["decline_politely", "refer_to_specialist", "manager", "website"],
  tenant_policy: ["require_owner_consent", "accept", "decline"],
  asbestos_policy: [
    "refer_to_licensed_asbestos_specialist",
    "accept",
    "refer_to_partner",
    "decline",
  ],
};

export const CANONICAL_TONE_STYLES = [
  "professional_friendly",
  "blue_collar_professional",
  "friendly_energetic",
  "professional_corporate",
];

export const LEGACY_TONE_STYLE_MAP = {
  blue_collar_professional: "professional_friendly",
  friendly_energetic: "professional_friendly",
  professional_corporate: "professional_friendly",
};

export const HANDOFF_TRIGGER_LEGACY_MAP = {
  human_requested_3_times: "customer_requests_human",
  customer_complaint_or_angry: "aggressive_behavior",
};

export const TENANT_POLICY_LEGACY_MAP = {
  request_owner_details: "require_owner_consent",
  accept: "accept",
  decline: "decline",
};

export const ASBESTOS_POLICY_LEGACY_MAP = {
  refer_to_partner: "refer_to_licensed_asbestos_specialist",
  accept: "accept",
  decline: "decline",
};

export const UNQUALIFIED_ACTION_LEGACY_MAP = {
  manager: "decline_politely",
  decline: "decline_politely",
  website: "website",
};

/** @type { bot_type: string, knowledge_base: object, qualification_logic: object, scheduling: object, tone_of_voice: object } */
export const UNIVERSAL_BOT_SETTINGS_DEFAULTS = {
  "bot_type": "universal_v1",
  "knowledge_base": {
    "company_name": "",
    "trading_name": "",
    "founded_year": 2014,
    "website_url": "",
    "company_phone": "",
    "company_email": "",
    "founders": [],
    "social_networks": [],
    "office_location": {
      "address": "",
      "is_customer_facing": true,
      "country": "",
      "state": "",
      "geo": {
        "lat": null,
        "lng": null,
      },
    },
    "service_area": {
      "zip_codes": [
        "any",
      ],
      "max_drive_radius_km": 80,
      "crosses_state_borders": false,
      "rejection_message": "We do not cover that area.",
      "regional_callouts": {
        "allowed": true,
        "min_job_value": "",
      },
    },
    "credentials": {
      "registration_number": "",
      "license_holder_name": "",
      "license_number": "",
      "insurance": "",
      "warranty_terms": "",
    },
    "legal_and_compliance": {
      "privacy_policy_url": "",
      "state_regulations": "",
    },
    "competitive_strategy": {
      "competitors_often_mentioned": [],
      "design_visualisation": {
        "offered": false,
        "stage_name": "",
        "short_description": "",
      },
      "core_differentiator": "",
      "unique_selling_points": [],
      "process_overview": "Enquiry, then consultation, then booking.",
    },
    "pricing_policy": {
      "quote_rules": {
        "free_quotes_standard": true,
        "fixed_price_guarantee_enabled": false,
        "fixed_price_guarantee_lock_description": "",
      },
      "job_requirements": {
        "minimum_job_value": "",
        "deposit_required": "",
      },
      "payment_and_finance": {
        "tax_status": "",
        "payment_methods": [
          "Bank Transfer",
          "Credit Card (Visa/Mastercard)",
          "Cash on completion milestones",
        ],
        "financing_options": {
          "offered": false,
          "provider_name": "",
        },
      },
      "ballpark_pricing": {
        "enforce_no_exact_prices": true,
        "products": [],
      },
    },
    "services_offered": {
      "example_service": {
        "name": "Example service",
        "status": "true",
        "synonyms": [
          "sample offering",
        ],
        "price_range": null,
      },
      "limited_service": {
        "name": "Limited service",
        "status": "sometimes",
        "synonyms": [],
        "price_range": null,
      },
      "unavailable_service": {
        "name": "Unavailable service",
        "status": "false",
        "synonyms": [],
        "price_range": null,
      },
    },
    "out_of_scope_services": [
      "unavailable_service",
    ],
    "emergency_safety_actions": {
      "default": {
        "phrases": [],
        "text": "Move to a safe place and wait for further instructions.",
      },
    },
    "handoff_settings": {
      "handoff_triggers": [
        "customer_requests_human",
        "aggressive_behavior",
        "complex_insurance_claim",
      ],
      "handoff_messages": {
        "general_escalation": "I'll connect you with the office. They will follow up during business hours.",
        "emergency_escalation": "I'm routing this to the office now.",
        "generic_failure": "We're having a brief technical issue. Please try again in a moment.",
      },
      "human_request_handoff_threshold": 3,
      "emergency_sla_text": "15 minutes",
      "roles": [
        {
          "type": "general",
          "role": "Office",
          "name": "",
          "phone": "",
          "email": "",
        },
      ],
    },
    "objection_handling": [],
  },
  "qualification_logic": {
    "lite_qualification_enabled": false,
    "booking_gate_keys": [
      "client_name",
      "contact_phone",
      "service_requested",
    ],
    "strict_qualification_gate": true,
    "skip_qualification_if_known_user": false,
    "interviewer_policy": {
      "repetition_soft_ask_threshold": 3,
      "refusal_escalation_threshold": 2,
    },
    "unqualified_action": "decline_politely",
    "questions": [
      {
        "question": "Can I get your name please?",
        "key": "client_name",
        "internal_goal": "Personalise the conversation",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "What's the best phone number to reach you on?",
        "key": "contact_phone",
        "internal_goal": "Collect a callback number",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "What's the best email to send your confirmation to?",
        "key": "contact_email",
        "internal_goal": "Collect an email address",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "What do you need help with?",
        "key": "service_requested",
        "internal_goal": "Match the request to the service catalog",
        "turn_on": true,
        "optional": false,
        "role": "service",
      },
    ],
    "disqualification_rules": {
      "blocked_property_types": [],
      "hazard_rules": {},
      "max_bridge_turns": 3,
      "rejection_messages": {
        "out_of_area": "We do not cover that area.",
        "service_not_offered": "We do not offer that service.",
        "property_type": "We are not able to take this request.",
        "budget_too_low": "Thanks for sharing your budget. We may not be the right fit for this request.",
        "default_polite_no": "Thank you for reaching out. We are not the right fit for this request.",
      },
      "site_visit_declined_phrases": [],
    },
  },
  "scheduling": {
    "meeting_type": "Free Consultation",
    "default_duration_minutes": 60,
    "channel_schedules": {
      "site_visit": {
        "monday": {
          "start": "07:00",
          "end": "15:00",
        },
        "tuesday": {
          "start": "07:00",
          "end": "15:00",
        },
        "wednesday": {
          "start": "07:00",
          "end": "15:00",
        },
        "thursday": {
          "start": "07:00",
          "end": "15:00",
        },
        "friday": null,
        "saturday": {
          "start": "08:00",
          "end": "12:00",
        },
        "sunday": null,
      },
      "phone_consult": {
        "monday": {
          "start": "08:30",
          "end": "17:00",
        },
        "tuesday": {
          "start": "08:30",
          "end": "17:00",
        },
        "wednesday": {
          "start": "08:30",
          "end": "17:00",
        },
        "thursday": {
          "start": "08:30",
          "end": "17:00",
        },
        "friday": {
          "start": "08:30",
          "end": "17:00",
        },
        "saturday": {
          "start": "09:00",
          "end": "13:00",
        },
        "sunday": null,
        "out_of_hours_message": "Our office is currently closed. For emergencies, call our on-call project manager — we'll respond within 15 minutes.",
      },
    },
    "operating_hours": {
      "public_holidays": [
        {
          "date": "2026-12-25",
          "name": "Christmas Day",
          "surcharge_fee": "$100",
        },
        {
          "date": "2026-12-26",
          "name": "Boxing Day",
          "surcharge_fee": "$100",
        },
        {
          "date": "2026-01-01",
          "name": "New Year's Day",
          "surcharge_fee": "$100",
        },
      ],
    },
    "logistics": {
      "travel_buffer_minutes": 30,
      "appointment_time_format": "arrival_window",
      "window_size_hours": 2,
      "max_appointments_per_day": 5,
    },
    "booking_rules": {
      "visit_min_lead_time_hours": 24,
      "call_min_lead_time_hours": 4,
      "max_lead_time_days": 30,
      "skip_weekday_conditions": {
        "enabled": false,
        "skip_weekday": "monday",
        "scope": "next_occurrence",
        "channels": [
          "site_visit",
        ],
        "apply_on_emergency": false,
        "when_client_writes": [],
      },
    },
    "booking_channel": {
      "visit_type": "free_quote_inspection",
      "call_type": "phone_video_consult",
      "default_channel": "site_visit",
      "visit_speech_fallback": "",
      "call_speech_fallback": "",
      "call_window_question": "",
      "plans_optional_line": "",
    },
    "media_requirements": {
      "default_photo_prompts": [
        "Wide shot showing the full room or affected area",
        "Close-up of the damage or detail you want addressed",
      ],
    },
    "team_calendars": [
      {
        "platform": "ghl",
        "calendar_id": "mock-universal-calendar-001",
        "label": "Consultant",
        "handles_appointment_types": [
          "free_quote_inspection",
        ],
      },
      {
        "platform": "ghl",
        "calendar_id": "mock-universal-office-001",
        "label": "Office",
        "handles_appointment_types": [
          "phone_video_consult",
        ],
      },
    ],
    "appointment_types": {
      "free_quote_inspection": {
        "is_offered": true,
        "label": "On-site visit",
        "speech": "On-site visit",
        "duration_minutes": 60,
        "is_free": true,
        "price": null,
        "attendee": "field_specialist",
        "prerequisites": [
          "address",
          "problem description",
        ],
        "closing_flow": "complimentary",
      },
      "phone_video_consult": {
        "is_offered": true,
        "label": "Phone or video call",
        "speech": "Phone or video call",
        "duration_minutes": 30,
        "is_free": true,
        "price": null,
        "attendee": "office_staff",
      },
    },
  },
  "tone_of_voice": {
    "bot_name": "Alex",
    "disclose_ai_status": true,
    "style": "professional_friendly",
    "use_emojis": false,
    "use_aussie_slang": false,
    "forbidden_phrases": [
      "100% covered",
    ],
    "allowed_brand_phrases": [],
    "preferred_phrases": [],
    "replacement_phrases": {
      "reach_out_scrub": [
        "Happy to keep going right here — what's the next step?",
      ],
      "scheduling_cta": [
        "I can get that scheduled — which day suits you?",
      ],
    },
    "emoji_policy": {
      "max_per_session": 2,
      "max_per_message": 1,
    },
    "tagline": "",
  },
};

export const RENOVATION_BOT_SETTINGS_DEFAULTS = UNIVERSAL_BOT_SETTINGS_DEFAULTS;

function cloneSharedBotSettingsDefaults() {
  return JSON.parse(JSON.stringify(UNIVERSAL_BOT_SETTINGS_DEFAULTS));
}

function channelAlreadyHasCalendarFlag(channels, key) {
  if (!channels || typeof channels !== "object" || Array.isArray(channels) || !(key in channels)) {
    return false;
  }
  const raw = channels[key];
  if (raw === null) return false;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return true;
  return Object.prototype.hasOwnProperty.call(raw, "enabled");
}

/** ON/ON only for a fresh universal pair. Does not add scheduling_disabled. */
function initializeNewUniversalCalendarControls(document) {
  if (!document || typeof document !== "object" || Array.isArray(document)) return {};
  if (document.bot_type !== "universal_v1") return document;
  const scheduling = document.scheduling;
  if (scheduling != null && (typeof scheduling !== "object" || Array.isArray(scheduling))) {
    return document;
  }
  const channels = scheduling && typeof scheduling === "object" ? scheduling.channel_schedules : null;
  if (channels != null && (typeof channels !== "object" || Array.isArray(channels))) return document;
  if (
    channelAlreadyHasCalendarFlag(channels, "site_visit")
    || channelAlreadyHasCalendarFlag(channels, "phone_consult")
  ) {
    return document;
  }
  const next = scheduling && typeof scheduling === "object" ? scheduling : {};
  if (next !== document.scheduling) document.scheduling = next;
  const stored = channels && typeof channels === "object" && !Array.isArray(channels) ? channels : {};
  if (stored !== next.channel_schedules) next.channel_schedules = stored;
  for (const key of ["site_visit", "phone_consult"]) {
    const raw = stored[key];
    const channel = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
    if (channel !== raw) stored[key] = channel;
    channel.enabled = true;
  }
  return document;
}

export function cloneUniversalDefaults() {
  return initializeNewUniversalCalendarControls(cloneSharedBotSettingsDefaults());
}

export function cloneRenovationDefaults() {
  return cloneSharedBotSettingsDefaults();
}
